import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsData } from '../work/Data';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './projectDetail.css';

gsap.registerPlugin(ScrollTrigger);

/* ─── Reusable components ─── */
const SectionLabel = ({ number, title }) => (
    <div className="pd-label">
        <span className="pd-label__num">{String(number).padStart(2, '0')}</span>
        <span className="pd-label__bar"></span>
        <span className="pd-label__title">{title}</span>
    </div>
);

const Tag = ({ children }) => <span className="pd-tag">{children}</span>;

const Placeholder = ({ hint }) => (
    <div className="pd-empty">
        <i className="pd-empty__icon uil uil-edit-alt"></i>
        <p className="pd-empty__text">{hint}</p>
    </div>
);

const ProjectDetail = () => {
    const { id } = useParams();
    const project = projectsData.find(p => p.id === parseInt(id));
    const wrapRef = useRef(null);

    const [isTocOpen, setIsTocOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    const tocItems = useMemo(() => {
        if (!project || !project.details) return [];
        const items = [];
        if (project.details.problems) {
            if (project.details.overview) items.push({ id: 'overview', label: 'Overview' });
            items.push({ id: 'challenges', label: 'Challenges' });
            if (project.details.personas) items.push({ id: 'personas', label: 'User Personas' });
            items.push({ id: 'solutions', label: 'The Solution' });
            if (project.details.userFlow) items.push({ id: 'user-flow', label: 'User Flow' });
            if (project.details.designSystem) items.push({ id: 'design-system', label: 'Design System' });
            if (project.details.tools) items.push({ id: 'tools-used', label: 'Tools Used' });
        } else {
            if (project.details.problem) items.push({ id: 'problem', label: 'The Problem' });
            if (project.details.solution) items.push({ id: 'solution', label: 'The Solution' });
            if (project.details.insights) items.push({ id: 'insights', label: 'Research & Insights' });
            if (project.details.impact) items.push({ id: 'impact', label: 'Impact & Results' });
            if (project.details.reflection) items.push({ id: 'reflection', label: 'Reflections' });
        }
        return items;
    }, [project]);

    /* Scroll to top on load */
    useEffect(() => {
        window.scrollTo(0, 0);
        setIsTocOpen(false);
        setActiveSection('');
    }, [id]);

    /* Highlight active TOC section on scroll */
    useEffect(() => {
        if (tocItems.length === 0) return;

        const observerOptions = {
            root: null,
            rootMargin: '-120px 0px -70% 0px',
            threshold: 0
        };

        const observerCallback = (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        tocItems.forEach(item => {
            const el = document.getElementById(item.id);
            if (el) observer.observe(el);
        });

        return () => {
            tocItems.forEach(item => {
                const el = document.getElementById(item.id);
                if (el) observer.unobserve(el);
            });
        };
    }, [tocItems, id]);

    const handleScrollTo = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setIsTocOpen(false);
        }
    };

    /* Animation effects using GSAP */
    useEffect(() => {
        if (!project) return;
        const ctx = gsap.context(() => {
            gsap.from('.pd-hero__title, .pd-hero__subtitle, .pd-hero__meta-item', {
                opacity: 0,
                y: 30,
                duration: 0.8,
                stagger: 0.15,
                ease: 'power3.out'
            });

            gsap.from('.pd-hero__cover', {
                opacity: 0,
                scale: 0.95,
                duration: 1,
                delay: 0.4,
                ease: 'power3.out'
            });

            const sections = document.querySelectorAll('.pd-section');
            sections.forEach(sec => {
                gsap.from(sec.querySelectorAll('.pd-label, .pd-section__content, .pd-grid-item'), {
                    scrollTrigger: {
                        trigger: sec,
                        start: 'top 80%',
                        toggleActions: 'play none none none'
                    },
                    opacity: 0,
                    y: 40,
                    duration: 0.8,
                    stagger: 0.2,
                    ease: 'power2.out'
                });
            });
        }, wrapRef);

        return () => ctx.revert();
    }, [id, project]);

    /* Get other projects for bottom recommendation */
    const moreProjects = useMemo(() => {
        if (!project) return [];
        return projectsData
            .filter(p => p.id !== project.id && p.category !== 'certificate')
            .sort(() => 0.5 - Math.random())
            .slice(0, 3);
    }, [project]);

    if (!project) {
        return (
            <div className="pd-not-found container">
                <h2>Project Not Found</h2>
                <p>The project you are looking for does not exist or has been removed.</p>
                <Link to="/" className="button button--flex">
                    Back to Home <i className="uil uil-arrow-left button__icon"></i>
                </Link>
            </div>
        );
    }

    return (
        <article className="pd-wrapper" ref={wrapRef}>
            {/* ─── Table of Contents Floating Shortcut & Absolute Modal ─── */}
            {tocItems.length > 0 && (
                <div className="pd-toc-container">
                    <button 
                        className={`pd-toc-toggle ${isTocOpen ? 'active' : ''}`}
                        onClick={() => setIsTocOpen(!isTocOpen)}
                        aria-label="Toggle Table of Contents"
                        title="Daftar Isi"
                    >
                        <i className={isTocOpen ? "uil uil-multiply" : "uil uil-list-ui-alt"}></i>
                    </button>

                    <div className={`pd-toc-modal ${isTocOpen ? 'open' : ''}`}>
                        <div className="pd-toc-header">
                            <h4 className="pd-toc-title">Daftar Isi</h4>
                            <button className="pd-toc-close" onClick={() => setIsTocOpen(false)}>
                                <i className="uil uil-multiply"></i>
                            </button>
                        </div>
                        <ul className="pd-toc-list">
                            {tocItems.map((item, index) => (
                                <li key={item.id} className="pd-toc-item">
                                    <button
                                        onClick={() => handleScrollTo(item.id)}
                                        className={`pd-toc-link ${activeSection === item.id ? 'active' : ''}`}
                                    >
                                        <span className="pd-toc-number">{String(index + 1).padStart(2, '0')}</span>
                                        <span className="pd-toc-text">{item.label}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {isTocOpen && <div className="pd-toc-overlay" onClick={() => setIsTocOpen(false)}></div>}
                </div>
            )}

            {/* ─── Hero Section ─── */}
            <header className="pd-hero container">
                <Link to="/?section=portfolio" className="pd-back-link">
                    <i className="uil uil-arrow-left"></i> Back to Portfolio
                </Link>
                <h1 className="pd-hero__title">{project.title}</h1>
                <div className="pd-hero__subtitle-wrapper">
                    <span className="pd-hero__subtitle">{project.category === "ui/ux" ? "UI/UX Case Study" : project.category.charAt(0).toUpperCase() + project.category.slice(1)}</span>
                </div>

                <div className="pd-hero__meta grid">
                    <div className="pd-hero__meta-item">
                        <span className="pd-hero__meta-label">Role</span>
                        <span className="pd-hero__meta-val">{project.role || "Designer & Developer"}</span>
                    </div>
                    <div className="pd-hero__meta-item">
                        <span className="pd-hero__meta-label">Client / Type</span>
                        <span className="pd-hero__meta-val">{project.client || "Personal Project"}</span>
                    </div>
                    <div className="pd-hero__meta-item">
                        <span className="pd-hero__meta-label">Duration</span>
                        <span className="pd-hero__meta-val">{project.duration || "2024"}</span>
                    </div>
                </div>

                <div className="pd-hero__cover">
                    <img src={project.image} alt={project.title} />
                </div>
            </header>

            {/* ─── Detail Content Sections ─── */}
            <div className="pd-body container">
                {project.details ? (
                    <>
                        {project.details.problems ? (
                            <>
                                {/* Overview Section */}
                                {project.details.overview && (
                                    <section className="pd-section pd-overview grid" id="overview">
                                        <SectionLabel number={1} title="Overview" />
                                        <div className="pd-section__content">
                                            <p className="pd-overview__text">{project.details.overview}</p>
                                        </div>
                                    </section>
                                )}

                                {/* Problems & Pain Points Section */}
                                <section className="pd-section pd-problems grid" id="challenges">
                                    <SectionLabel number={2} title="The Challenges" />
                                    <div className="pd-section__content">
                                        {project.details.problemStatement && (
                                            <p className="pd-problems__intro">{project.details.problemStatement}</p>
                                        )}
                                        <div className="pd-problems__grid grid">
                                            {project.details.problems.map((prob, idx) => (
                                                <div className="pd-problem-card" key={idx}>
                                                    <div className="pd-problem-card__num">0{idx + 1}</div>
                                                    <h4 className="pd-problem-card__title">{prob.title}</h4>
                                                    <p className="pd-problem-card__desc">{prob.desc}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>

                                {/* User Personas Section */}
                                {project.details.personas && (
                                    <section className="pd-section pd-personas grid" id="personas">
                                        <SectionLabel number={3} title="User Personas" />
                                        <div className="pd-section__content">
                                            <div className="pd-personas__grid grid">
                                                {project.details.personas.map((pers, idx) => (
                                                    <div className="pd-persona-card" key={idx}>
                                                        <div className="pd-persona-card__header">
                                                            <div className="pd-persona-card__icon-wrap">
                                                                <i className={`${pers.avatar} pd-persona-card__icon`}></i>
                                                            </div>
                                                            <div className="pd-persona-card__header-info">
                                                                <h4 className="pd-persona-card__name">{pers.name}</h4>
                                                                <span className="pd-persona-card__age-role">{pers.role} • {pers.age} thn</span>
                                                            </div>
                                                        </div>
                                                        <p className="pd-persona-card__bio">{pers.bio}</p>
                                                        <div className="pd-persona-card__details grid">
                                                            <div className="pd-persona-card__details-col">
                                                                <h5>Kebutuhan & Tujuan</h5>
                                                                <ul>
                                                                    {pers.goals.map((g, gi) => <li key={gi}>{g}</li>)}
                                                                </ul>
                                                            </div>
                                                            <div className="pd-persona-card__details-col">
                                                                <h5>Hambatan & Frustrasi</h5>
                                                                <ul>
                                                                    {pers.pains.map((p, pi) => <li key={pi}>{p}</li>)}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </section>
                                )}

                                {/* Solution & Core Features Section */}
                                <section className="pd-section pd-solutions grid" id="solutions">
                                    <SectionLabel number={4} title="The Solution" />
                                    <div className="pd-section__content">
                                        {project.details.solutions && (
                                            <p className="pd-solutions__intro">{project.details.solutions}</p>
                                        )}
                                        {project.details.features && (
                                            <div className="pd-features__grid grid">
                                                {project.details.features.map((feat, idx) => (
                                                    <div className="pd-feature-card" key={idx}>
                                                        <div className="pd-feature-card__icon-wrap">
                                                            <i className={`${feat.icon} pd-feature-card__icon`}></i>
                                                        </div>
                                                        <h4 className="pd-feature-card__title">{feat.title}</h4>
                                                        <p className="pd-feature-card__desc">{feat.desc}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </section>

                                {/* User Flow Section */}
                                {project.details.userFlow && (
                                    <section className="pd-section pd-flow grid" id="user-flow">
                                        <SectionLabel number={5} title="User Flow" />
                                        <div className="pd-section__content">
                                            <p className="pd-flow__intro">Alur Pengalaman Pengguna (E-Ticketing Flow):</p>
                                            <div className="pd-flow__container">
                                                {project.details.userFlow.map((flow, idx) => (
                                                    <React.Fragment key={idx}>
                                                        <div className="pd-flow-step">
                                                            <div className="pd-flow-step__badge">{flow.step}</div>
                                                            <h5 className="pd-flow-step__label">{flow.label}</h5>
                                                            <p className="pd-flow-step__desc">{flow.desc}</p>
                                                        </div>
                                                        {idx < project.details.userFlow.length - 1 && (
                                                            <div className="pd-flow-arrow">
                                                                <i className="bx bx-right-arrow-alt"></i>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    </section>
                                )}

                                {/* Design System Section */}
                                {project.details.designSystem && (
                                    <section className="pd-section pd-design-system grid" id="design-system">
                                        <SectionLabel number={6} title="Design System" />
                                        <div className="pd-section__content">
                                            <div className="pd-design-system__colors">
                                                <h4 className="pd-design-system__section-title">Color Palette</h4>
                                                <div className="pd-colors-grid grid">
                                                    {project.details.designSystem.colors.map((color, idx) => (
                                                        <div className="pd-color-swatch" key={idx}>
                                                            <div 
                                                                className="pd-color-swatch__preview" 
                                                                style={{ backgroundColor: color.hex }}
                                                            ></div>
                                                            <div className="pd-color-swatch__info">
                                                                <h5 className="pd-color-swatch__name">{color.name}</h5>
                                                                <span className="pd-color-swatch__hex">{color.hex}</span>
                                                                <span className="pd-color-swatch__type">{color.type}</span>
                                                                <p className="pd-color-swatch__desc">{color.desc}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {project.details.designSystem.typography && (
                                                <div className="pd-design-system__typography">
                                                    <h4 className="pd-design-system__section-title">Typography</h4>
                                                    <div className="pd-typography-display">
                                                        <div className="pd-typography-display__preview">
                                                            <div className="pd-typography-specimen">Aa</div>
                                                            <div className="pd-typography-info">
                                                                <h5>Font Family: {project.details.designSystem.typography.fontFamily}</h5>
                                                                <p className="pd-typography-desc">
                                                                    {project.details.designSystem.typography.description}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className="pd-typography-weights">
                                                            <div className="pd-typography-weight-item light">Outfit Light (300) - The quick brown fox jumps over the lazy dog.</div>
                                                            <div className="pd-typography-weight-item regular">Outfit Regular (400) - The quick brown fox jumps over the lazy dog.</div>
                                                            <div className="pd-typography-weight-item medium">Outfit Medium (500) - The quick brown fox jumps over the lazy dog.</div>
                                                            <div className="pd-typography-weight-item semibold">Outfit Semibold (600) - The quick brown fox jumps over the lazy dog.</div>
                                                            <div className="pd-typography-weight-item bold">Outfit Bold (700) - The quick brown fox jumps over the lazy dog.</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {/* Tools Used Section */}
                                {project.details.tools && (
                                    <section className="pd-section pd-tools grid" id="tools-used">
                                        <SectionLabel number={7} title="Tools Used" />
                                        <div className="pd-section__content">
                                            <div className="pd-tools-wrap">
                                                {project.details.tools.map((tool, idx) => (
                                                    <span className="pd-tool-badge" key={idx}>
                                                        <i className="bx bx-check-double"></i> {tool}
                                                    </span>
                                                ))}
                                            </div>
                                            {project.link && (
                                                <div className="pd-live-button-wrap">
                                                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="button button--flex">
                                                        Lihat Portofolio Lengkap di Behance <i className="uil uil-external-link-alt button__icon"></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Problem Section */}
                                {project.details.problem && (
                                    <section className="pd-section pd-problem grid" id="problem">
                                        <SectionLabel number={1} title="The Problem" />
                                        <div className="pd-section__content">
                                            <p>{project.details.problem}</p>
                                        </div>
                                    </section>
                                )}

                                {/* Solution Section */}
                                {project.details.solution && (
                                    <section className="pd-section pd-solution grid" id="solution">
                                        <SectionLabel number={2} title="The Solution" />
                                        <div className="pd-section__content">
                                            <p>{project.details.solution}</p>
                                        </div>
                                    </section>
                                )}

                                {/* Insights/Process Section */}
                                {project.details.insights && (
                                    <section className="pd-section pd-insights grid" id="insights">
                                        <SectionLabel number={3} title="Research & Insights" />
                                        <div className="pd-section__content">
                                            <p>{project.details.insights}</p>
                                        </div>
                                    </section>
                                )}

                                {/* Impact Section */}
                                {project.details.impact && (
                                    <section className="pd-section pd-impact grid" id="impact">
                                        <SectionLabel number={4} title="Impact & Results" />
                                        <div className="pd-section__content">
                                            <p>{project.details.impact}</p>
                                        </div>
                                    </section>
                                )}

                                {/* Reflections Section */}
                                {project.details.reflection && (
                                    <section className="pd-section pd-reflection grid" id="reflection">
                                        <SectionLabel number={5} title="Reflections" />
                                        <div className="pd-section__content">
                                            <p>{project.details.reflection}</p>
                                        </div>
                                    </section>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    /* Default/Fallback details when details object is missing from Data.jsx */
                    <section className="pd-section pd-fallback grid" id="project-details">
                        <SectionLabel number={1} title="Project Details" />
                        <div className="pd-section__content">
                            <p className="pd-fallback-text">
                                This project represents a high-quality {project.category} work.
                                Detailed design case study process and product metrics are available upon request.
                            </p>
                            {project.link && (
                                <a href={project.link} target="_blank" rel="noopener noreferrer" className="button button--flex">
                                    View Live Prototype / Code <i className="uil uil-external-link-alt button__icon"></i>
                                </a>
                            )}
                        </div>
                    </section>
                )}

                {/* More Projects Section */}
                <div className="pd-more">
                    <h2 className="pd-more__title">More Projects</h2>
                    <span className="pd-more__sub">Explore other works</span>
                    <div className="pd-more__grid grid">
                        {moreProjects.map(p => (
                            <Link to={`/project/${p.id}`} key={p.id} className="pd-more__card">
                                <div className="pd-more__img-wrap">
                                    <img src={p.image} alt={p.title} />
                                    <div className="pd-more__overlay">
                                        <span>View Case Study →</span>
                                    </div>
                                </div>
                                <div className="pd-more__info">
                                    <h3>{p.title}</h3>
                                    <span>{p.category.toUpperCase()}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </article>
    );
};

export default ProjectDetail;
